import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation
} from "@angular/core";
import {Base} from "../../../../../../../../common/interfaces/base.interfaces";
import {ConfirmComponent} from "../../../../../../../../common/components/confirm/confirm.component";
import {Warehouse} from "../../../../../../../../common/interfaces/warehouse.interface";
import {ToastService} from "../../../../../../../../common/services/toast.service";
import {SpinnerService} from "../../../../../../../../common/services/spinner.service";
import {Api3Service} from "../../../../../../../../common/services/api3.service";
import {Table2Component} from "../../../../../../../../common/components/table2";
import {Table} from "../../../../../../../../common/interfaces/table.interface";
import {Modal, ModalService} from "../../../../../../../services/modal.service";
import {CommonFormComponent} from "../../../../../../../../common/components/form";
import {Form} from "../../../../../../../../common/interfaces/form.interface";
import ISelectOption = Form.ISelectOption;
import IPallet = Warehouse.IPallet;

@Component({
    selector: "section-warehouse-order-wizard-shuffle-pallets",
    template: `
        <common-table2 *ngIf="tableSettings" [settings]="tableSettings" (onRowsSelected)="selectedRows = $event">
            <div row1>
                <button mat-raised-button type="button" [disabled]="!selectedRows.length" color="accent"
                        (click)="linkSelected()">
                    Link selected
                </button>
            </div>
        </common-table2>
        <common-confirm></common-confirm>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None
})
export class WarehouseProceduresWizardShufflePalletsComponent implements OnInit, OnDestroy, OnChanges {

    protected destroy$: EventEmitter<boolean> = new EventEmitter(false);

    public selectedRows: any[] = [];

    public tableSettings: Table.ISettings;

    @ViewChild(ConfirmComponent, {static: true})
    public confirmRef: ConfirmComponent;

    @ViewChild(Table2Component, {static: false})
    public tableRef: Table2Component;

    @Input()
    public shipment_id: number;

    @Input()
    public state: Base.IState;

    @Input()
    public packages: Warehouse.IPackage[];

    @Input()
    public selected_package_name: string = null;

    public constructor(
        protected changeDetectorRef: ChangeDetectorRef,
        private api3Service: Api3Service,
        private toastService: ToastService,
        private spinnerService: SpinnerService,
        private modalService: ModalService,
    ) {
    }

    private url(): Table.ITableApi {
        return {
            url: [this.state.section, "shipments", "" + this.shipment_id, "transactions", "pallets"],
            query: {
                relations: [
                    "WarehousePackages:warehouse_packages.id,warehouse_packages.name",
                    "Boxes:id,pallet_id"
                ],
                with_transaction_qty: true,
                with_orders: true,
                warehouse_package: this.selected_package_name ?? null,
            },
            version: 3
        };
    }

    private prepareTable(): void {
        this.tableSettings = {
            api: this.url(),
            columns: [
                {
                    data: "id",
                    title: "Packages",
                    sortable: false,
                    searchable: false,
                    render: (row: any): string => {
                        if (!row.warehouse_packages.length) {
                            return "NOT LINKED";
                        }
                        return row.warehouse_packages.map((p: Warehouse.IPackage): string => p.name).join(", ");
                    },
                    cssClassFn: (row: any): string => {
                        return !row.warehouse_packages.length ? "text-error" : "";
                    },
                },
                {
                    data: "ref",
                    title: "Pallet ref"
                },
                {
                    data: "id",
                    title: "Item",
                    sortable: false,
                    searchable: false,
                    render: (row: any): string => {
                        return row.warehouse_transactions.length ? row.warehouse_transactions[0].item : "";
                    },
                },
                {
                    data: "warehouse_transactions_sum_quantity",
                    title: "Quantity",
                    sortable: false,
                    searchable: false,
                },
                {
                    data: "id",
                    title: "Order",
                    sortable: false,
                    searchable: false,
                    render: (row: any): string => {
                        const orders: any[] = [
                            ...new Set(row.warehouse_transactions
                                .map((p: Warehouse.ITransaction): string => p.order.ref))
                        ];
                        return orders.join(", ");
                    },
                },
            ],
            actions: [
                {
                    name: "link",
                    title: "Link",
                    click: async (row: any): Promise<any> => {
                        await this.linkPallet(row);
                        this.toastService.show("Pallet updated", "success");
                        this.tableRef.reload();
                    },
                },
            ],
            multi_select: true
        };
    }

    private async selectPackage(): Promise<string> {
        const res: Modal.IResponse = await this.modalService.open(CommonFormComponent, {
            formConfig: {
                name: "Link all boxes from this pallet to package",
                description: "",
                fields: [
                    {
                        label: "Package name",
                        name: "size",
                        size: "full",
                        type: "select",
                        values: this.packages
                            .filter((p: Warehouse.IPackage): boolean => p.name !== this.selected_package_name)
                            .map((p: Warehouse.IPackage): ISelectOption => ({
                                name: p.name,
                                value: p.name
                            })),
                        selected_on_top: false,
                        required: true,
                    },
                ],
            },
            noReset: true
        });

        return res.value ?? null;
    }


    public async linkPallet(pallet: IPallet, warehouse_package_name: string = null): Promise<any> {

        if (!warehouse_package_name) {
            warehouse_package_name = await this.selectPackage();
        }

        if (!warehouse_package_name) {
            return;
        }

        this.spinnerService.show();
        for (const box of pallet.boxes) {
            await this.api3Service.put(
                `${this.state.section}/boxes/${box.id}/warehouse-transactions-package`,
                {
                    warehouse_package_name,
                    shipment_id: this.shipment_id
                }
            );
        }
        this.spinnerService.hide();

    }

    public async linkSelected(): Promise<any> {
        const warehouse_package_name: string = await this.selectPackage();

        if (!warehouse_package_name) {
            return;
        }

        for (const row of this.selectedRows) {
            await this.linkPallet(row, warehouse_package_name);
        }

        this.toastService.show("Pallets updated", "success");
        this.tableRef.reload();
    }

    public ngOnInit(): void {
        this.prepareTable();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (this.tableSettings
            && changes.selected_package_name
            && changes.selected_package_name.currentValue !== changes.selected_package_name.previousValue
        ) {
            this.tableRef.reload(this.url());
        }
    }

    public ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }

}
